/**
 * chatHelpers — Pure helper functions for chat message parsing & detection.
 * Extracted from ChatContext.jsx so it stays a thin provider.
 */

import {
  MAX_CONTEXT_MESSAGE_CHARS,
  MAX_CONTEXT_MESSAGES,
  DRAFTING_INTENT_KEYWORDS,
  DRAFTING_FLOW_MARKERS,
  NDA_COLLECTION_PROMPT_MARKERS,
} from './chatConstants';

export const generateId = () => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const isArabicText = (text) => {
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

export const sanitizeText = (text) => {
  if (!text) return '';
  const cleaned = text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ');
  return cleaned.replace(/[ \t]{2,}/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
};

export const removeInvisibleDraftWarmupChars = (text) => {
  return (text || '').replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
};

// Sep 2026 — chat/matter title sanitiser. The sidebar used to show
// raw JSON keys ('"body": "##...') and markdown fences ("```markdown")
// when the user's first message happened to be a pasted structured
// block. Strip that noise before persisting the title. Fallback to a
// typed default so no chat ends up untitled in the sidebar.
const _MD_FENCE_RE = /^```[\w+-]*\s*$/;
const _JSON_KEY_PREFIX_RE = /^\s*"[A-Za-z_][A-Za-z0-9_]*"\s*:\s*"?/;
const _HEADING_MARKER_RE = /^[#>*\-•·\s`]+/;
const _TRAILING_JSON_NOISE_RE = /["',\s]+$/;
const _MONTH_ABBR_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const _MONTH_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];

export const sanitizeTitle = (raw, { lang = 'en', maxLen = 50 } = {}) => {
  const fallbackFor = () => {
    const now = new Date();
    const day = now.getDate();
    const monthLbl = lang === 'ar' ? _MONTH_AR[now.getMonth()] : _MONTH_ABBR_EN[now.getMonth()];
    return lang === 'ar' ? `مراجعة عقد · ${day} ${monthLbl}` : `Contract review · ${day} ${monthLbl}`;
  };
  if (!raw) return fallbackFor();
  // Take the first non-empty, non-fence line as the candidate.
  const lines = String(raw).split('\n').map((l) => l.trim()).filter(Boolean);
  let line = '';
  for (const cand of lines) {
    if (_MD_FENCE_RE.test(cand)) continue;
    line = cand;
    break;
  }
  if (!line) return fallbackFor();
  line = line.replace(_JSON_KEY_PREFIX_RE, '');
  line = line.replace(_HEADING_MARKER_RE, '');
  line = line.replace(_TRAILING_JSON_NOISE_RE, '');
  line = line.replace(/\s+/g, ' ').trim();
  if (!line || line.length < 4) return fallbackFor();
  return line.slice(0, maxLen);
};

export const extractErrorFromText = (text) => {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed?.error || parsed?.detail || null;
    } catch {
      return null;
    }
  }
  return null;
};

export const extractSuggestionFromResponse = (text, language) => {
  if (!text) return '';
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return '';

  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (language === 'ar') {
      if (line.startsWith('هل') && line.endsWith('؟')) return line;
    } else if (line.toLowerCase().startsWith('would you like') && line.endsWith('?')) {
      return line;
    }
  }
  return '';
};

export const extractSourcesFromResponse = (text) => {
  if (!text) return [];
  const lines = text.split('\n').map((line) => line.trim());
  const headerIndex = lines.findIndex(
    (line) => line.startsWith('**المصادر:**') || line.startsWith('**Sources:**')
  );
  if (headerIndex === -1) return [];

  const sources = [];
  for (let i = headerIndex + 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line) continue;
    if (line.startsWith('**')) break;
    if (line.startsWith('•')) sources.push(line.replace(/^•\s*/, '').trim());
  }
  return sources;
};

export const extractLawSystemsFromSources = (sources) => {
  const systems = new Set();
  sources.forEach((source) => {
    const parts = source.split(' - ');
    const lawName = parts[0]?.trim();
    if (lawName) systems.add(lawName);
  });
  return Array.from(systems);
};

export const detectModeForTracking = (question, usesDocument) => {
  const text = (question || '').toLowerCase();
  const reviewKeywords = ['راجع', 'حلل', 'مراجعة', 'review', 'analyze', 'analysis', 'check', 'افحص', 'قيم'];
  const draftingKeywords = ['صياغة', 'اكتب', 'كتابة', 'draft', 'contract', 'agreement', 'letter', 'nda'];
  if (!usesDocument) {
    if (draftingKeywords.some((k) => text.includes(k))) return 'DRAFTING';
    return 'LEGAL_QA';
  }
  if (reviewKeywords.some((k) => text.includes(k))) return 'DOCUMENT_REVIEW';
  return 'DOCUMENT_QA';
};

export const hasDocumentReviewIntent = (text) => {
  const normalized = (text || '').toLowerCase();
  if (!normalized) return false;
  const reviewKeywords = [
    'راجع', 'راجعي', 'مراجعة', 'حلل', 'تحليل', 'افحص', 'قيم', 'تقييم',
    'review', 'analyze', 'analysis', 'check', 'examine', 'assess', 'evaluate',
    'راجع العقد', 'راجع المستند', 'راجع الوثيقة', 'حلل العقد',
    'review the contract', 'review this contract', 'analyze the contract',
  ];
  return reviewKeywords.some((k) => normalized.includes(k.toLowerCase()));
};

export const hasDraftingIntent = (text) => {
  const normalized = (text || '').toLowerCase();
  if (!normalized) return false;
  return DRAFTING_INTENT_KEYWORDS.some((k) => normalized.includes(k.toLowerCase()));
};

export const hasStrongDraftSignal = (text) => {
  const normalized = (text || '').toLowerCase();
  if (!normalized) return false;
  return /(صياغة|اكتب|صغ|عقد|اتفاقية|سرية|عدم\s*إفشاء|عدم\s*افشاء|nda|contract|agreement|confidentiality)/i.test(normalized);
};

export const looksLikeLongLegalDraft = (text) => {
  const normalized = (text || '').toLowerCase();
  if (!normalized) return false;
  if (normalized.length < 900) return false;
  const legalMarkers = [
    'المادة', 'اتفاقية', 'عدم إفشاء', 'عدم افشاء',
    'confidentiality agreement', 'article', 'governing law',
    'وثيقة العقد الأساسية', 'القسم الأول', 'شروط العقد',
  ];
  return legalMarkers.some((k) => normalized.includes(k.toLowerCase()));
};

export const hasDraftingFlowContext = (messages) => {
  const recent = (messages || []).slice(-8);
  if (!recent.length) return false;
  return recent.some((msg) => {
    const content = (msg?.content || '').toLowerCase();
    if (!content) return false;
    return DRAFTING_FLOW_MARKERS.some((m) => content.includes(m.toLowerCase()));
  });
};

export const isLegacyNdaCollectionPrompt = (text) => {
  const normalized = (text || '').toLowerCase();
  if (!normalized) return false;
  const hits = NDA_COLLECTION_PROMPT_MARKERS.filter((m) => normalized.includes(m.toLowerCase()));
  return hits.length >= 2;
};

export const buildApiMessages = (messages) => {
  const filtered = messages
    .filter((msg) => (msg.role === 'user' || msg.role === 'assistant') && msg.type !== 'file')
    .map((msg) => {
      const rawContent = typeof msg.content === 'string' ? msg.content : String(msg.content || '');
      const content = sanitizeText(rawContent);
      return {
        role: msg.role,
        content: content.length > MAX_CONTEXT_MESSAGE_CHARS
          ? content.slice(0, MAX_CONTEXT_MESSAGE_CHARS)
          : content,
      };
    })
    .filter((msg) => msg.content.trim().length > 0);

  return filtered.slice(-MAX_CONTEXT_MESSAGES);
};

export const buildDocumentContext = (content, document) => {
  const sanitizedContent = sanitizeText(content);
  const safeContent = sanitizedContent.length > MAX_CONTEXT_MESSAGE_CHARS
    ? sanitizedContent.slice(0, MAX_CONTEXT_MESSAGE_CHARS)
    : sanitizedContent;

  if (!document?.text) return safeContent;

  const isArabic = isArabicText(safeContent);
  const docLabel = isArabic ? 'محتوى المستند المرفق:' : 'Attached document content:';
  const truncatedNote = isArabic
    ? '(ملاحظة: تم اختصار المستند بسبب الحجم)'
    : '(Note: Document truncated due to size)';

  const header = `${safeContent}\n\n${docLabel}\n`;
  let remaining = MAX_CONTEXT_MESSAGE_CHARS - header.length;
  if (remaining <= 0) return safeContent.slice(0, MAX_CONTEXT_MESSAGE_CHARS);

  let docText = sanitizeText(document.text);
  let didTruncate = false;
  if (docText.length > remaining) {
    docText = docText.slice(0, Math.max(0, remaining));
    didTruncate = true;
  }

  let finalDocText = docText;
  if ((document.truncated || didTruncate) && finalDocText.length > 0) {
    const note = `\n${truncatedNote}`;
    if (header.length + finalDocText.length + note.length <= MAX_CONTEXT_MESSAGE_CHARS) {
      finalDocText += note;
    }
  }
  return `${safeContent}\n\n${docLabel}\n${finalDocText}`;
};
